<template>
  <el-form
    ref="formRef"
    require-asterisk-position="right"
    label-position="top"
    :model="form"
    :rules="formRules"
  >
    <el-form-item
      prop="fileUrl"
      :label="`合同信息（${fileList.length}/1）`"
    >
      <el-upload
        class="mt-4 w-[978px] upload-demo"
        drag
        :accept="typeStrs"
        :headers="{ Authorization:`Bearer ${getToken()}` }"
        :file-list="fileList"
        :limit="1"
        :action="UploadFileUrls.normal"
        :on-error="() => { loading = false }"
        :before-upload="onBeforeUpload"
        :on-remove="(file: any) => { removeUploaded(file) }"
        :on-success="(res: any) => { resolveUploaded(res) }"
        multiple
      >
        <svg-icon class="!w-[60px] !h-[60px] ml-[5px] text-[#A8ABB2]" icon="material-symbols:backup-rounded" />
        <div class="">
          将文件拖至此区域 <span class="main-primary-text">或点击上传</span>
        </div>
        <template #tip>
          <div class="el-upload__tip">
            支持doc、docx、wps、pdf、xls、xlsx、jpg、jpeg、bmp、png格式，单份文档不超过50MB
          </div>
        </template>
      </el-upload>
    </el-form-item>
    <div class="flex">
      <div class="oa-form-left label">
        <el-form-item prop="contractSubject" label="合同主题">
          <div class="tip2">
            <el-popover
              width="380"
              placement="right"
              trigger="hover"
            >
              <template #reference>
                <svg-icon class="!w-[14px] ml-[15px] main-primary-text" icon="mingcute:warning-line" />
              </template>
              <div class="text-[##6B7891]">
                合同主题：此合同的名称 <br>
                建议格式：【业务类型】-【合同类型】-【对方名称】<br>
                示例：“撮合-自由职业者合同-李三” 
              </div>
            </el-popover>
          </div>
          <el-input
            v-model="form.contractSubject"
            clearable
            autocomplete="nony"
            placeholder="请输入"
            max-length="100"
          />
        </el-form-item>
      </div>
      <div class="oa-form-right label">
        <el-form-item prop="expireTime" label="合同到期日期">
          <div class="tip">
            <el-popover
              width="353"
              placement="right"
              trigger="hover"
            >
              <template #reference>
                <svg-icon class="!w-[14px] ml-[15px] main-primary-text" icon="mingcute:warning-line" />
              </template>
              <div class="text-[##6B7891]">
                合同到期日期为签署合同的履约结束日期，不影响签署过程
              </div>
            </el-popover>
          </div>
          <el-date-picker
            v-model="form.expireTime"
            style="width: 100%;"
            type="date"
            clearable
            placeholder="请选择日期"
            format="YYYY-MM-DD"
            value-format="YYYY-MM-DD HH:mm:ss"
          />
        </el-form-item>
      </div>
    </div>
  </el-form>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { SvgIcon } from '@vue3/components';
import { EAxiosResponseCode, getToken } from '@core/api';
import { ElMessage, UploadProps } from 'element-plus';
import { UploadFileUrls } from '../../../../../api';

defineProps({
  enableEnterKey: {
    type: Boolean,
    default: true,
  },
  /** 自动滚动到错误位置 */
  autoScrollToError: {
    type: Boolean,
    default: true,
  },
});

const typeStrs = '.doc,.docx,.wps,.pdf,.xls,.xlsx,.jpg,.jpeg,.bmp,.png';
const form = ref({} as any);
const fileList = ref([]);
const loading = ref(false);
const formRef = ref();

const formRules = {
  contractSubject: [
    {
      required: true,
      message: '请输入合同主题',
      trigger: 'blur',
    },
  ],
  expireTime: [
    {
      required: true,
      message: '请选择合同到期日期',
      trigger: 'blur',
    },
  ],
  fileUrl: [
    {
      required: true,
      message: '请上传doc、docx、wps、pdf、xls、xlsx、jpg、jpeg、bmp、png格式文件',
      trigger: 'blur',
    },
  ],
};

const onBeforeUpload: UploadProps['beforeUpload'] = (file) => {
  loading.value = false;
  const fileExt = file.name.split('.').pop()
    .toLocaleLowerCase();

  const fileType = ['doc', 'docx', 'wps', 'pdf', 'xls', 'xlsx', 'jpg', 'jpeg', 'bmp', 'png'];

  if (!fileType.includes(fileExt)) {
    ElMessage.error('只能上传doc、docx、wps、pdf、xls、xlsx、jpg、jpeg、bmp、png格式文件');
    return false;
  } else if (file.size / 1024 / 1024 > 50) {
    ElMessage.error('文件大小不能超过50MB');
    return false;
  }
  loading.value = true;
  return true;
};

const resolveUploaded = (res: any) => {
  const { code, context, message } = res || {};

  loading.value = false;

  if (String(code) === EAxiosResponseCode.Succeed) {
    fileList.value = [
      {
        name: context.fileName,
        url: context.fileUrl,
        fileName: context.fileName,
        fileUrl: context.fileUrl,
      },
    ];
    form.value.fileUrl = context.fileUrl;
    form.value.contractSubject = context.fileName;
  } else {
    ElMessage.error(message);
  }
};

const removeUploaded = (file: any) => {
  fileList.value = [];
  form.value.fileUrl = '';
};

async function validate(callback?: () => void) {
  let valid = false;

  if (!formRef.value) return valid;

  await formRef.value.validate((v) => {
    if (v) {
      valid = true;
      callback && callback();
    }
  });

  return valid;
}

function resetFormValues() {

  formRef.value?.resetFields?.();
}

defineExpose({
  /**
   * 信息校验
   * return {boolean} true 通过， false失败
  */
  validate,
  /** 获取表单数据 */
  getValues: () => form.value,
  /** 表单数据重置（base props.data） */
  reset: resetFormValues,
});

</script>

<style lang="scss" scoped>
.oa-form-left {
  width: 432px;
}

.oa-form-right {
  width: 432px;
  margin-left: 114px;
}

.label {
  position: relative;

  & .tip {
    position: absolute;
    top: -30px;
    left: 85px;
  }
  & .tip2 {
    position: absolute;
    top: -30px;
    left: 58px;
  }
}

</style>
